import { useMemo } from 'react'

import { matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TaskListTemplateModel, TaskListTemplateRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['taskListTemplates']

type TaskListTemplateFilters = {
  brand_id?: number,
  hidden?: boolean,
  template_checklist?: boolean,
  string?: string,
}

type UseTaskListTemplatesOptions = {
  filters?: TaskListTemplateFilters,
  performHttpRequests?: boolean,
  requestOptions?: TaskListTemplateRequestOptions,
}

function useTaskListTemplates(options: UseTaskListTemplatesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    brand_id: filterBrandId,
    hidden: filterHidden,
    string: filterString,
    template_checklist: filterTemplateChecklist,
  } = filters

  const {
    updatedEntities: { taskListTemplates: taskListTemplatesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { taskListTemplates } = useSelector(reduxState => reduxState.entities)

  const filteredTaskListTemplates = useMemo(() => {
    const filtered = Object.values(taskListTemplates).filter((taskListTemplate: TaskListTemplateModel) => {
      const {
        hidden, id, owner_id, owner_type, template_checklist, title,
      } = taskListTemplate

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchBrand = filterBrandId ? owner_type === 'Brand' && owner_id === filterBrandId : true
      const matchHidden = filterHidden === hidden
      const matchTemplateChecklist = filterTemplateChecklist === template_checklist

      return matchBrand && matchHidden && matchString && matchTemplateChecklist
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [taskListTemplatesUpdatedAt, JSON.stringify(filters)])

  const filteredTaskListTemplatesCount = filteredTaskListTemplates.length
  const hasFilteredTaskListTemplates = !!filteredTaskListTemplatesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTaskListTemplatesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingTaskListTemplates } = useReduxAction(
    'taskListTemplates',
    'loadTaskListTemplates',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTaskListTemplates,
    filteredTaskListTemplatesCount,
    hasFilteredTaskListTemplates,
    loading: loadingTaskListTemplates,
  }
}

export default useTaskListTemplates
